/**
 * AIAnalyst — port of the AI ANALYST section in layout.py + ai callbacks.
 * 11 quick-command buttons, alert quick-add strip, chat output area,
 * input row. Posts to /api/copilot/ask (Workers AI, Build #1).
 */
import { useState, useRef, useEffect } from 'react';
import { useStore } from '@/store';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { ORG, MUT, FONT } from '@/theme';
import type { ChatMessage, AlertRow } from '@/types';

const QUICK_BTNS: Array<[string, string]> = [
  ['Analyze',   'Analyze $SYMBOL — give me a clean trade setup'],
  ['Market',    'What is the current market regime?'],
  ['Scan WL',   'Run a breakout scan on my watchlist'],
  ['Options',   'Show options flow + GEX for $SYMBOL'],
  ['Breakouts', 'List near-term breakout candidates'],
  ['Pivots',    'Give me pivot points for $SYMBOL'],
  ['Port Risk', 'Assess my portfolio risk vs SPY'],
  ['IV Rank',   'Show IV rank for $SYMBOL'],
  ['Regime',    'Classify the macro regime right now'],
  ['Earnings',  'Earnings plays this week'],
  ['Help',      'What can you do?'],
];

export function AIAnalyst() {
  const focus       = useStore(s => s.focusSymbol);
  const watchlist   = useStore(s => s.watchlist);
  const messages    = useStore(s => s.messages);
  const isStreaming = useStore(s => s.isStreaming);
  const addMessage  = useStore(s => s.addMessage);
  const setStreaming= useStore(s => s.setStreaming);
  const clearChat   = useStore(s => s.clearChat);
  const alerts      = useStore(s => s.alerts);
  const addAlert    = useStore(s => s.addAlert);
  const removeAlert = useStore(s => s.removeAlert);

  const [input, setInput] = useState('');
  const [altSym, setAltSym] = useState('');
  const [altPx,  setAltPx]  = useState<number | ''>('');
  const [altDir, setAltDir] = useState<'above' | 'below'>('above');
  const [altNote, setAltNote] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text: string) => {
    if (!text.trim() || isStreaming) return;
    const final = text.replace(/\$SYMBOL/g, focus);
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user', content: final, timestamp: Date.now(),
    };
    addMessage(userMsg);
    setInput('');
    setStreaming(true);
    try {
      const r = await api<{ ok: boolean; answer: string; model: string; provider?: string; tokens_in?: number; tokens_out?: number }>(
        '/api/copilot/ask',
        {
          method: 'POST',
          body: JSON.stringify({
            question: final,
            includePortfolio: true,
            watchlist: [focus, ...watchlist.filter(s => s !== focus).slice(0, 11)],
          }),
        },
      );
      addMessage({
        id: crypto.randomUUID(), role: 'assistant',
        content: r.answer || '(no response)',
        timestamp: Date.now(), model: r.model,
        // Show BILLED tokens (in + out). Showing tokens_out alone made
        // each call look ~20x cheaper than it is: every question ships
        // the live portfolio/watchlist context as input, which dominates.
        tokens: (r.tokens_in ?? 0) + (r.tokens_out ?? 0),
        tokensIn: r.tokens_in ?? 0,
        tokensOut: r.tokens_out ?? 0,
        provider: r.provider,
      });
    } catch (e) {
      toast.error(`AI failed: ${(e as Error).message}`);
      addMessage({
        id: crypto.randomUUID(), role: 'assistant',
        content: `Error: ${(e as Error).message}`,
        timestamp: Date.now(),
      });
    } finally {
      setStreaming(false);
    }
  };

  const onAddAlert = () => {
    if (!altSym || typeof altPx !== 'number') return;
    const a: AlertRow = {
      id: crypto.randomUUID(),
      symbol: altSym.toUpperCase(),
      price: altPx,
      direction: altDir,
      note: altNote || undefined,
    };
    addAlert(a);
    setAltSym(''); setAltPx(''); setAltNote('');
    // HONEST TOAST. This alert lives in an in-memory Zustand array: there
    // is no endpoint, no evaluation loop, and no persistence. It will not
    // fire, and it disappears on refresh. Saying "Alert added" invited a
    // trader to rely on it as a price watch — which is exactly the sort of
    // thing someone leaves running and walks away from.
    toast(
      `Watch noted: ${a.symbol} ${altDir} $${altPx}\n` +
      `This is a local note only — it does NOT trigger and is lost on refresh.`,
      { icon: '⚠', duration: 6000 },
    );
  };

  return (
    <>
      {/* Quick command buttons */}
      <div className="quick-btns">
        {QUICK_BTNS.map(([lbl, prompt]) => (
          <button key={lbl} className="quick-btn" onClick={() => send(prompt)} disabled={isStreaming}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Alert quick-add strip */}
      <div style={{
        display: 'flex', gap: 3, padding: '4px 6px',
        flexWrap: 'wrap', alignItems: 'center',
        borderBottom: '1px solid #222',
      }}>
        <input placeholder="Sym" value={altSym} onChange={e => setAltSym(e.target.value.toUpperCase())}
               style={{ width: 52, height: 20, fontSize: 10 }} />
        <input type="number" placeholder="Price" value={altPx}
               onChange={e => setAltPx(e.target.value ? Number(e.target.value) : '')}
               style={{ width: 68, height: 20, fontSize: 10 }} />
        <select value={altDir} onChange={e => setAltDir(e.target.value as 'above' | 'below')}
                style={{ width: 68, height: 20, fontSize: 10 }}>
          <option value="above">Above</option>
          <option value="below">Below</option>
        </select>
        <input placeholder="Note" value={altNote} onChange={e => setAltNote(e.target.value)}
               style={{ width: 70, height: 20, fontSize: 10 }} />
        <button onClick={onAddAlert} style={{
          background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none',
          fontFamily: FONT, fontSize: 9, fontWeight: 'bold',
          padding: '2px 5px', borderRadius: 2,
        }}>+ ALERT</button>
      </div>
      {alerts.length > 0 && (
        <div style={{ maxHeight: 64, overflowY: 'auto', padding: '2px 6px', borderBottom: '1px solid #222' }}>
          {/* These do not fire. Label them, permanently and in place —
              a trader who scrolls past the toast must still be able to
              tell that nothing is watching the tape for them. */}
          <div style={{ fontSize: 9, color: '#a06000', letterSpacing: 0.5 }}>
            LOCAL NOTES — NOT MONITORED, CLEARED ON REFRESH
          </div>
          {alerts.map(a => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888' }}>
              <span>{a.symbol} {a.direction} ${a.price}{a.note ? ` — ${a.note}` : ''}</span>
              <button onClick={() => removeAlert(a.id)} style={{ background: 'none', border: 'none', color: '#333' }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* AI output */}
      <div className="ai-chat">
        {messages.length === 0 && (
          <div style={{ color: MUT }}>
            <div style={{ color: ORG, marginBottom: 8 }}>OMNISTRAT AI ANALYST</div>
            Ask me anything: analyze, options flow, portfolio risk,
            backtest, pairs, seasonality, iv rank, regime, order flow,
            squeeze...
            {'\n\n'}Examples:
            {'\n'}  analyze NVDA
            {'\n'}  options flow AAPL
            {'\n'}  pairs AAPL MSFT
            {'\n'}  portfolio risk
            {'\n'}  gex NVDA
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{ marginBottom: 8 }}>
            <span style={{ color: m.role === 'user' ? ORG : '#888', fontWeight: 'bold' }}>
              {m.role === 'user' ? '> ' : '◆ '}
            </span>
            <span>{m.content}</span>
            {m.tokens != null && (
              <div
                style={{ color: MUT, fontSize: 9, marginTop: 2 }}
                title={`${m.provider ?? ''} ${m.model ?? ''}${m.tokensIn != null ? ` — ${m.tokensIn} in (live context) + ${m.tokensOut} out` : ''}`}
              >
                {/* Provider + a SHORT model name. Full ids like
                    "@cf/meta/llama-3.3-70b-instruct-fp8-fast" are unreadable
                    at 9px and vary per provider — full detail is on hover. */}
                {m.provider ? m.provider + ' · ' : ''}
                {(m.model ?? '').split('/').pop()?.slice(0, 28) ?? ''} · {m.tokens} tok
                {m.tokensIn != null ? ` (${m.tokensIn} in / ${m.tokensOut} out)` : ''}
              </div>
            )}
          </div>
        ))}
        {isStreaming && <div style={{ color: ORG }}>◆ thinking...</div>}
        <div ref={bottomRef} />
      </div>

      {/* Input row */}
      <div className="ai-input-row">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(input); }}
          placeholder={`Ask about ${focus}...`}
          style={{ flex: 1, fontSize: 11, padding: '4px 8px', height: 26 }}
          disabled={isStreaming}
        />
        <button onClick={() => send(input)} disabled={isStreaming || !input.trim()} style={{
          background: ORG, color: '#000', border: 'none',
          fontFamily: FONT, fontSize: 10, fontWeight: 'bold',
          padding: '2px 12px', borderRadius: 2, whiteSpace: 'nowrap',
        }}>ASK</button>
        {messages.length > 0 && (
          <button onClick={clearChat} title="Clear chat" style={{
            background: 'none', border: '1px solid #222', color: '#444',
            fontFamily: FONT, fontSize: 9, padding: '2px 6px',
          }}>CLR</button>
        )}
      </div>
    </>
  );
}
